import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Col, Stack } from "react-bootstrap";
import Lottie from "lottie-react";
import { useTranslation } from "react-i18next";

import "./TurnOnNotificationsPage.scss";
import PrimaryActionButton from "../../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import useNotifications from "../../../hooks/useNotifications";
import NotificationFlow from "../../../components/notification-flow/NotificationFlow";
import { OnboardingSteps, ROUTES } from "../../../utils/constants";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";
import NotificationsAnimation from "../../../assets/animations/stack-notifications.json";
import DownloadAppPrompt, {
  PROMPT_DOWNLOAD_SCENARIOS,
} from "../../../components/DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../../hooks/useDisclosure";
import { useAtomValue } from "jotai";
import { userAtom } from "../../../jotai/atoms/user";
import useAnalytics from "../../../hooks/useAnalytics";
import { NotificationOptionProperty } from "../../../utils/analytics";
import OnboardingContainer from "../../../components/onboarding/OnboardingContainer/OnboardingContainer";
import SecondaryActionButton from "../../../components/Buttons/SecondaryActionButton/SecondaryActionButton";

function TurnOnNotificationsPage() {
  const navigate = useNavigate();
  const user = useAtomValue(userAtom);
  const notifications = useNotifications();
  const { goToStep } = useOnboardingSteps();
  const location = useLocation();
  const analytics = useAnalytics();
  const { t } = useTranslation("common", { keyPrefix: "notifications" });
  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();

  const isOnLoginRoute = location.pathname.includes(ROUTES.ROOT.LOG_IN);
  const isReturningUser = user?.isReturning || isOnLoginRoute;

  const handleTurnOnNotifications = () => {
    analytics.sendNotificationOptionsEvent(
      NotificationOptionProperty.ENABLE_NOTIFICATIONS,
    );
    notifications.handleNotifyMe();
  };

  const handleSkip = () => {
    analytics.sendNotificationOptionsEvent(NotificationOptionProperty.SKIP);
    if (isReturningUser) {
      navigate(`${ROUTES.ROOT.DASHBOARD}`);
    } else {
      goToStep(OnboardingSteps.VIEW_INSURANCE_BONUS);
    }
  };

  return (
    <OnboardingContainer className="TurnOnNotificationsPage">
      <div>
        <Stack gap={2}>
          <h1 className="heading">
            {t("hyper_works_best_with_notifications")}.
          </h1>
          <p className="subheading">
            {t("be_the_first_to_know_about_tournaments_with_huge_victory_pots")}
            .
          </p>
        </Stack>
        <Col className="notifications-animation-container">
          <Lottie animationData={NotificationsAnimation} loop={true} />
        </Col>
      </div>

      <Stack
        gap={3}
        className="d-flex justify-content-center ctasContainer mt-0"
      >
        <PrimaryActionButton
          size="lg"
          text={t("enable_notifications")}
          onClick={handleTurnOnNotifications}
        />
        <SecondaryActionButton
          text={t("maybe_later")}
          buttonClassName="maybeLaterButton"
          onClick={handleSkip}
        />
      </Stack>

      <NotificationFlow
        {...notifications}
        handleGetApp={openDownloadAppPrompt}
      />
      <DownloadAppPrompt
        isOpen={isDownloadAppPromptOpen}
        onOpen={openDownloadAppPrompt}
        onClose={closeDownloadAppPrompt}
        showNotificationsFlow={false}
        promptDownloadScenario={PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.OTHERS}
      />
    </OnboardingContainer>
  );
}

export default TurnOnNotificationsPage;
